import React, { useState } from "react";
import { Loader2, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SiteShell from "../components/site/SiteShell";
import { Container, Eyebrow, Button } from "../components/site/ui";
import { Reveal } from "../components/site/Reveal";
import { api, formatApiErrorDetail } from "../lib/api";

const empty = { name: "", company: "", email: "", phone: "", message: "" };

export default function PayMyBill() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder:text-zinc-500 focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/20 transition";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { toast.error("Please add your name, email and request."); return; }
    setLoading(true);
    try {
      await api.post("/billing-request", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <SiteShell title="Pay My Bill — GoBuildWeb" description="Request a secure invoice or payment link from GoBuildWeb. We'll send you a secure link to pay — no card details entered on this site." testId="pay-my-bill-page">
      <section className="pt-[72px]">
        <Container className="grid items-start gap-12 py-16 md:py-24 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <Eyebrow>Client Billing</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-white md:text-5xl">Pay my bill</h1>
            <p className="mt-4 max-w-md text-zinc-400">Request a secure invoice or payment link. Submit your details below and we'll email you a secure link to complete your payment.</p>
            <div className="mt-8 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <ShieldCheck size={20} className="mt-0.5 shrink-0 text-gold-500" />
              <p className="text-sm text-zinc-300">For your security, we never collect card or bank details on this website. We'll send a secure, encrypted payment link separately.</p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8">
              {done ? (
                <div className="py-10 text-center" data-testid="billing-success">
                  <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-gold-500/15 text-gold-400"><CheckCircle2 size={32} /></span>
                  <h2 className="mt-5 font-display text-2xl font-bold text-white">Request received</h2>
                  <p className="mx-auto mt-3 max-w-sm text-zinc-400">We'll email you a secure payment link shortly. Thanks!</p>
                  <div className="mt-6 flex justify-center"><Button to="/">Back home</Button></div>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="billing-form">
                  <input className={input} placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="bill-name" />
                  <input className={input} placeholder="Business name" value={form.company} onChange={set("company")} data-testid="bill-business" />
                  <input type="email" className={input} placeholder="Email *" value={form.email} onChange={set("email")} data-testid="bill-email" />
                  <input className={input} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="bill-phone" />
                  <textarea rows={4} className={`${input} sm:col-span-2 resize-none`} placeholder="Billing request / message *" value={form.message} onChange={set("message")} data-testid="bill-message" />
                  <button type="submit" disabled={loading} className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 px-6 py-3.5 font-semibold text-black transition hover:bg-gold-400 disabled:opacity-60" data-testid="bill-submit">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                    {loading ? "Sending…" : "Request Payment Link"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </Container>
      </section>
    </SiteShell>
  );
}
